/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to WARRANTIES of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect, useCallback } from 'react';
import type { Product } from '@shared/types/products';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { getCodeIndexStatusByProduct } from '../../../services/skillshop/code-index';
import {
  Bot,
  FileText,
  Loader2,
  Play,
  RefreshCw,
  Search,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
} from 'lucide-react';

export interface CodeIndexStatus {
  status: 'indexed' | 'not_indexed' | 'indexing' | 'error' | 'unavailable' | 'unknown' | 'loading' | 'not_configured';
  isAvailable: boolean;
  fileCount?: number;
  chunkCount?: number;
  lastIndexed?: string;
  lastUpdated?: string;
  progress?: number;
  indexStatus?: 'completed' | 'limit_reached';
  lastAttemptedProgress?: number;
  languages?: string[];
  message?: string;
}

export interface DocumentationTabProps {
  product: Product;
  isIndexing: boolean;
  isRunningDocAgent: boolean;
  isExecuting: boolean;
  docAgentOutput: string | null;
  onIndexCodebase: (forceReindex: boolean) => Promise<void>;
  onRunDocAgent: () => void;
}

export function DocumentationTab({
  product,
  isIndexing,
  isRunningDocAgent,
  isExecuting,
  docAgentOutput,
  onIndexCodebase,
  onRunDocAgent,
}: DocumentationTabProps) {
  // Internal state for code index status - loaded on-demand when tab is selected
  const [codeIndexStatus, setCodeIndexStatus] = useState<CodeIndexStatus>({
    status: 'loading',
    isAvailable: false
  });

  // Fetch code index status on mount and when product changes
  const fetchCodeIndexStatus = useCallback(async (isPolling = false) => {
    // Only show loading state on initial fetch, not during polling
    if (!isPolling) {
      setCodeIndexStatus(prev => ({ ...prev, status: 'loading' }));
    }
    
    try {
      const result = await getCodeIndexStatusByProduct(product.id);
      
      setCodeIndexStatus({
        status: result.status === 'not_configured' ? 'not_indexed' : result.status,
        isAvailable: result.mcp_connected,
        fileCount: result.file_count,
        chunkCount: result.chunk_count,
        lastIndexed: result.last_indexed,
        lastUpdated: result.last_updated,
        progress: result.progress,
        indexStatus: result.index_status,
        lastAttemptedProgress: result.last_attempted_progress,
        message: result.message
      });
    } catch (error) {
      console.error('Error fetching code index status:', error);
      setCodeIndexStatus({
        status: 'error',
        isAvailable: false,
        message: 'Failed to fetch code index status'
      });
    }
  }, [product.id]);

  // Fetch status on mount
  useEffect(() => {
    fetchCodeIndexStatus();
  }, [fetchCodeIndexStatus]);

  // Smart polling: only poll during active indexing, use longer interval
  // When status is stable (indexed, not_indexed, error), no need to poll
  useEffect(() => {
    // Don't poll if status is stable (not actively changing)
    if (
      codeIndexStatus.status === 'indexed' ||
      codeIndexStatus.status === 'not_indexed' ||
      codeIndexStatus.status === 'not_configured' ||
      codeIndexStatus.status === 'error' ||
      codeIndexStatus.status === 'unavailable'
    ) {
      return;
    }

    // Poll every 5 seconds only during active indexing or loading
    const intervalId = setInterval(() => {
      fetchCodeIndexStatus(true); // Pass true to indicate polling (won't show loading state)
    }, 5000);

    return () => clearInterval(intervalId);
  }, [fetchCodeIndexStatus, codeIndexStatus.status]);

  // Check if indexing was interrupted (service unavailable but snapshot shows indexing)
  const isIndexingInterrupted = !codeIndexStatus.isAvailable && codeIndexStatus.status === 'indexing';

  // Helper to render the status icon
  const renderStatusIcon = () => {
    // If service is unavailable and status is indexing, show paused icon
    if (isIndexingInterrupted) {
      return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    }
    
    switch (codeIndexStatus.status) {
      case 'indexed':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'indexing':
      case 'loading':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'not_indexed':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'unavailable':
        return <AlertCircle className="h-5 w-5 text-muted-foreground" />;
      default:
        return <AlertCircle className="h-5 w-5 text-muted-foreground" />;
    }
  };

  // Helper to get status label and color
  const getStatusInfo = () => {
    // If service is unavailable and status is indexing, show as interrupted
    if (isIndexingInterrupted) {
      return { label: 'Interrupted', color: 'bg-yellow-100 text-yellow-800' };
    }
    
    switch (codeIndexStatus.status) {
      case 'indexed':
        return { label: 'Indexed', color: 'bg-green-100 text-green-800' };
      case 'indexing':
        return { label: 'Indexing...', color: 'bg-blue-100 text-blue-800' };
      case 'loading':
        return { label: 'Checking...', color: 'bg-gray-100 text-gray-800' };
      case 'not_indexed':
        return { label: 'Not Indexed', color: 'bg-yellow-100 text-yellow-800' };
      case 'error':
        return { label: 'Error', color: 'bg-red-100 text-red-800' };
      case 'unavailable':
        return { label: 'Unavailable', color: 'bg-gray-100 text-gray-600' };
      default:
        return { label: 'Unknown', color: 'bg-gray-100 text-gray-600' };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="space-y-6">
      {/* Code Index Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            <CardTitle>Code Index</CardTitle>
          </div>
          <CardDescription>
            Semantic code search index for AI-powered code discovery
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status Display */}
          <div className="border rounded-lg p-4 bg-muted/30">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                {renderStatusIcon()}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-medium">Index Status</h3>
                  <Badge variant="secondary" className={statusInfo.color}>
                    {statusInfo.label}
                  </Badge>
                  {!codeIndexStatus.isAvailable && codeIndexStatus.status !== 'loading' && (
                    <Badge variant="outline" className="text-xs text-muted-foreground">
                      Service Unavailable
                    </Badge>
                  )}
                </div>
                
                {/* Progress Bar for Indexing */}
                {codeIndexStatus.status === 'indexing' && (
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-muted-foreground">
                        {isIndexingInterrupted ? 'Indexing was interrupted' : 'Indexing in progress...'}
                      </span>
                      <span className="font-medium">{codeIndexStatus.progress?.toFixed(1) ?? 0}%</span>
                    </div>
                    <Progress value={codeIndexStatus.progress ?? 0} className={`h-2 ${isIndexingInterrupted ? 'opacity-60' : ''}`} />
                    {codeIndexStatus.lastUpdated && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {isIndexingInterrupted ? 'Last updated: ' : 'Updated: '}
                        {new Date(codeIndexStatus.lastUpdated).toLocaleString()}
                      </p>
                    )}
                    {isIndexingInterrupted && (
                      <p className="text-xs text-yellow-600 mt-1">
                        Enable the claude-context server to resume indexing
                      </p>
                    )}
                  </div>
                )}
                
                {codeIndexStatus.status === 'indexed' && (
                  <div className="mb-2">
                    <p className="text-sm text-muted-foreground">
                      {codeIndexStatus.fileCount ? `${codeIndexStatus.fileCount} files` : ''}
                      {codeIndexStatus.fileCount && codeIndexStatus.chunkCount ? ' / ' : ''}
                      {codeIndexStatus.chunkCount ? `${codeIndexStatus.chunkCount} chunks` : ''} indexed
                      {codeIndexStatus.languages && codeIndexStatus.languages.length > 0 && (
                        <span className="ml-2">
                          ({codeIndexStatus.languages.slice(0, 3).join(', ')}{codeIndexStatus.languages.length > 3 ? '...' : ''})
                        </span>
                      )}
                    </p>
                    {codeIndexStatus.indexStatus === 'limit_reached' && (
                      <p className="text-xs text-yellow-600 mt-1">
                        Index limit reached - some files may not be indexed
                      </p>
                    )}
                  </div>
                )}
                
                {codeIndexStatus.lastIndexed && (
                  <p className="text-xs text-muted-foreground mb-2">
                    Last indexed: {new Date(codeIndexStatus.lastIndexed).toLocaleString()}
                  </p>
                )}
                
                {/* Show error status with last attempted progress */}
                {codeIndexStatus.status === 'error' && codeIndexStatus.lastAttemptedProgress !== undefined && (
                  <div className="mb-2">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-red-600">Indexing failed at:</span>
                      <span className="font-medium text-red-600">{codeIndexStatus.lastAttemptedProgress.toFixed(1)}%</span>
                    </div>
                    <Progress value={codeIndexStatus.lastAttemptedProgress} className="h-2 opacity-60" />
                  </div>
                )}
                
                {codeIndexStatus.message && codeIndexStatus.status !== 'indexed' && codeIndexStatus.status !== 'indexing' && (
                  <p className="text-sm text-muted-foreground mb-2">
                    {codeIndexStatus.message}
                  </p>
                )}
                
                <div className="flex flex-wrap gap-2 mt-3">
                  {product.repositoryUrl && (
                    <>
                      <Button
                        size="sm"
                        onClick={async () => {
                          await onIndexCodebase(false);
                          // Refresh status after indexing completes
                          fetchCodeIndexStatus();
                        }}
                        disabled={isIndexing || codeIndexStatus.status === 'indexing' || !codeIndexStatus.isAvailable}
                        className="gap-2"
                      >
                        {isIndexing ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Indexing...
                          </>
                        ) : codeIndexStatus.status === 'indexed' ? (
                          <>
                            <RefreshCw className="h-4 w-4" />
                            Re-index
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4" />
                            Index Codebase
                          </>
                        )}
                      </Button>
                      
                      {codeIndexStatus.status === 'indexed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            await onIndexCodebase(true);
                            // Refresh status after indexing completes
                            fetchCodeIndexStatus();
                          }}
                          disabled={isIndexing || !codeIndexStatus.isAvailable}
                          className="gap-2"
                        >
                          <RefreshCw className="h-4 w-4" />
                          Force Re-index
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => fetchCodeIndexStatus()}
                        disabled={codeIndexStatus.status === 'loading'}
                        className="gap-2"
                      >
                        <RefreshCw className={`h-4 w-4 ${codeIndexStatus.status === 'loading' ? 'animate-spin' : ''}`} />
                        Refresh Status
                      </Button>
                    </>
                  )}
                </div>
                
                {!product.repositoryUrl && (
                  <p className="text-xs text-destructive mt-2">
                    A repository URL is required to enable code indexing.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* About Code Index */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium">About Code Index</h4>
            <p className="text-sm text-muted-foreground">
              The code index enables semantic search across your codebase. Once indexed, AI agents 
              can use natural language queries to find relevant code, understand your project structure, 
              and provide more accurate assistance.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Documentation Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            <CardTitle>Documentation</CardTitle>
          </div>
          <CardDescription>
            Manage and generate documentation for this product
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Documentation Agent Section */}
          <div className="border rounded-lg p-4 bg-muted/30">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Bot className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium mb-1">Documentation Initializer Agent</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Automatically analyze your codebase and generate comprehensive documentation 
                  including technical design documents, API documentation, and architecture overviews.
                </p>
                
                <Button
                  onClick={onRunDocAgent}
                  disabled={isRunningDocAgent || isExecuting || !product.repositoryUrl}
                  className="gap-2"
                >
                  {isRunningDocAgent ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Running Agent...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      Run Documentation Agent
                    </>
                  )}
                </Button>

                {!product.repositoryUrl && (
                  <p className="text-xs text-destructive mt-2">
                    A repository URL is required to run the documentation agent.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Agent Output */}
          {docAgentOutput && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Agent Output</label>
              <div className="p-4 bg-muted rounded-lg max-h-96 overflow-y-auto">
                <pre className="text-sm whitespace-pre-wrap font-mono">
                  {docAgentOutput}
                </pre>
              </div>
            </div>
          )}

          <Separator />

          {/* Documentation Status */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium">Documentation Status</h3>
            <p className="text-sm text-muted-foreground">
              Documentation is generated in the <code className="bg-muted px-1 py-0.5 rounded">docs/</code> directory 
              of your repository. The agent will create or update the following files:
            </p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { file: 'docs/product/OVERVIEW.md', desc: 'Product overview' },
                { file: 'docs/product/FEATURES.md', desc: 'Feature catalog' },
                { file: 'docs/technical/TDD.md', desc: 'Technical design' },
                { file: 'docs/technical/DATABASE.md', desc: 'Database schema' },
                { file: 'docs/technical/FRONTEND.md', desc: 'Frontend architecture' },
                { file: 'docs/technical/BACKEND.md', desc: 'Backend architecture' },
              ].map(({ file }) => (
                <div key={file} className="flex items-center gap-2 text-xs">
                  <FileText className="h-3 w-3 text-muted-foreground" />
                  <code className="text-muted-foreground">{file}</code>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
